/**
 * `registerCoreProviders()` (PLAN §12.1 "Registration") — called once from
 * App.js when the app is past the sign-in gate. Each provider file
 * default-exports `create(deps) → Provider`, so tests build them with fakes;
 * this file wires the real dependencies.
 *
 * INDEX PROVIDERS: tasks / boards / documents / requests / collections /
 * calendar are builder B's, over the local search index (`../index`:
 * `indexClient` → the Rust `palette_*` commands on desktop, `webIndexClient`
 * on web). Tests pass `overrides.indexClient`.
 */
import createCommands from './commands';
import createPages from './pages';
import createSettings from './settings';
import createTabs from './tabs';
import createHelp from './help';
import createPeople from './people';
import createChats from './chats';
import createMessages from './messages';
import { createPeopleIndex } from './peopleIndex';
import { createWorkspacesProvider, createEnvironmentsProvider, createProjectsProvider, createTeamsProvider } from './workspaces';
import { createIndexProviders } from '../index';
import { registerProvider } from '../registry';
import { registerCoreCommands } from '../commands/coreCommands';
import { useChatStore } from '../../store/chatStore';
import { useChatSearchStore } from '../../store/chatSearchStore';
import { useTeamsStore } from '../../store/teamsStore';
import chatService from '../../services/chatService';
import chatSearchAllService from '../../services/chatSearchAllService';
import teamsService from '../../services/teamsService';
import { openChatTarget } from '../../utils/chatOpen';
import i18n from '../../i18n';

/**
 * Open a conversation and jump to a message once its view has mounted AND its
 * first page of messages is in the store. The jump reads the loaded messages
 * (and pages back from the oldest one); requested before the first page
 * arrived it found nothing and ended as "That message is no longer
 * available" — seen against the real backend, where that page takes a
 * round trip.
 */
export async function openMessage(channelId, seq, { timeoutMs = 3000, loadTimeoutMs = 8000 } = {}) {
  await openChatTarget({ channelId });
  if (seq == null) return;
  const started = Date.now();
  const until = (ready, ms) =>
    new Promise((resolve) => {
      const tick = () => {
        if (ready() || Date.now() - started > ms) resolve();
        else setTimeout(tick, 40);
      };
      tick();
    });
  // The message view resets the search store for a new conversation on mount
  // (useJumpToMessage); a request made before that would be wiped.
  await until(() => String(useChatSearchStore.getState().channelId) === String(channelId), timeoutMs);
  await until(() => {
    const byChannel = useChatStore.getState().messagesByChannel || {};
    const list = byChannel[channelId] || byChannel[String(channelId)] || byChannel[Number(channelId)] || [];
    return list.some((m) => m?.server_seq != null);
  }, loadTimeoutMs);
  useChatSearchStore.getState().requestJump(channelId, seq);
}

/** Team rosters, once per session, in idle time (§3.6). */
function makeRosterLoader(index) {
  let started = false;
  return function loadTeamRosters() {
    if (started) return;
    started = true;
    const run = () => {
      for (const team of useTeamsStore.getState().teams || []) {
        if (team?.id == null) continue;
        teamsService
          .getTeamMembers(team.id)
          .then((res) => index.addPeople(Array.isArray(res) ? res : res?.data || [], 'team', { teamName: team.name || null }))
          .catch(() => {});
      }
    };
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 0);
  };
}

let registered = null;
let peopleIndex = null;

export function getPeopleIndex() {
  return peopleIndex;
}

/**
 * @param {object} [overrides] dependency overrides (tests)
 * @returns {() => void} unregister everything
 */
export function registerCoreProviders(overrides = {}) {
  if (registered) return registered;
  const t = (k, o) => i18n.t(k, o);
  peopleIndex =
    overrides.peopleIndex ||
    createPeopleIndex({ getChannels: () => useChatStore.getState().channels, getCurrentUserId: () => useChatStore.getState().currentUserId, t });
  const openChat = overrides.openChat || openChatTarget;
  const providers = [
    createCommands(),
    createPages(),
    createSettings(),
    createTabs(),
    createHelp(),
    createWorkspacesProvider(),
    createEnvironmentsProvider(),
    createProjectsProvider(),
    createTeamsProvider(),
    createPeople({
      index: peopleIndex,
      searchUsers: overrides.searchUsers || ((q, o) => chatService.searchUsers(q, o)),
      openChat,
      loadTeamRosters: overrides.loadTeamRosters || makeRosterLoader(peopleIndex),
    }),
    createChats({
      getChannels: () => useChatStore.getState().channels,
      getTeams: () => useTeamsStore.getState().teams || [],
      openChat,
      setMuted: async (id, muted) => {
        const ch = await chatService.setMuted(id, muted);
        if (ch) useChatStore.getState().upsertChannel(ch);
      },
      markRead: (id, seq) => {
        useChatStore.getState().clearUnread(id);
        if (seq != null) chatService.markRead(id, seq).catch(() => {});
      },
    }),
    createMessages({
      search: overrides.chatSearch || ((q, o) => chatSearchAllService.search(q, o)),
      openMessage: overrides.openMessage || openMessage,
    }),
    ...createIndexProviders(overrides.indexClient || undefined, overrides.indexDeps || {}),
  ];
  const offs = providers.map((p) => registerProvider(p));
  offs.push(registerCoreCommands());
  registered = () => {
    offs.forEach((off) => off());
    registered = null;
    peopleIndex = null;
  };
  return registered;
}

/** Sign-out: drop in-memory indexes (§3.3 "Per account"). */
export function resetPaletteIndexes() {
  peopleIndex?.reset();
}
